import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import type { QuickNodeCoords } from "../../utils/workspace.types";
import { useUnitStore } from "../../../../store/useUnitStore";

interface QuickNodeDialogProps {
  isOpen: boolean;
  coords: QuickNodeCoords;
  onCoordChange: (axis: "x" | "y" | "z", value: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  // Optional CSV inputs for batch creation (deprecated behavior removed)
  coordinateCsvValues?: Record<string, any>;
  onCoordinateCsvChange?: (axis: "x" | "y" | "z", value: string) => void;
  // Offset mode options
  offsetEnabled?: boolean;
  offsetCount?: number;
  offsetValues?: QuickNodeCoords;
  onSetOffsetEnabled?: (value: boolean) => void;
  onSetOffsetCount?: (value: number) => void;
  onSetOffsetValue?: (axis: "x" | "y" | "z", value: number) => void;
}

/**
 * Quick Node Dialog component - dialog for quick node creation
 * Extracted from STAADWorkspace3D to maintain exact behavior
 */
export const QuickNodeDialog: React.FC<QuickNodeDialogProps> = ({
  isOpen,
  coords,
  onCoordChange,
  onConfirm,
  onCancel,
  coordinateCsvValues,
  onCoordinateCsvChange,
  offsetEnabled = false,
  offsetCount = 1,
  offsetValues = { x: 0, y: 0, z: 0 },
  onSetOffsetEnabled,
  onSetOffsetCount,
  onSetOffsetValue,
}) => {
  const { lengthUnit } = useUnitStore();

  // Helper function for unit label
  const getLengthUnitLabel = (): string => {
    switch (lengthUnit) {
      case "inch":
        return "IN";
      case "foot":
        return "FT";
      case "millimeter":
        return "MM";
      case "centimeter":
        return "CM";
      case "decimeter":
        return "DM";
      case "meter":
        return "M";
      case "kilometer":
        return "KM";
      default:
        return lengthUnit;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      PaperProps={{
        style: {
          minWidth: "350px",
          padding: "20px",
        },
      }}
    >
      <DialogContent>
        <Typography variant="h6" gutterBottom>
          📍 Create Node
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Enter precise coordinates for the new node:
        </Typography>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm();
          }}
        >
          {(["x", "y", "z"] as const).map((axis) => (
            <div key={axis} style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                {axis.toUpperCase()}:
              </label>
              <div style={{ position: "relative" }}>
                <TextField
                  // Keep the same appearance/layout as before
                  value={
                    coordinateCsvValues && coordinateCsvValues[axis]
                      ? coordinateCsvValues[axis]
                      : String(coords[axis])
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    // If the input contains separators (comma, semicolon or whitespace), treat as CSV list
                    if (onCoordinateCsvChange && /[,;\s]/.test(value)) {
                      onCoordinateCsvChange(axis, value);
                    } else {
                      // Try to parse as number and call onCoordChange
                      const num = parseFloat(value);
                      onCoordChange(axis, isNaN(num) ? 0 : num);
                      // Also clear any CSV stored value for this axis
                      if (onCoordinateCsvChange)
                        onCoordinateCsvChange(axis, "");
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onConfirm();
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    style: { paddingRight: "40px" },
                  }}
                />
                {/* Unit label */}
                <span
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "14px",
                    color: "#999",
                    pointerEvents: "none",
                  }}
                >
                  {getLengthUnitLabel()}
                </span>
              </div>
            </div>
          ))}

          {/* Offset mode checkbox and inputs (show/hide inputs based on checkbox) */}
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={offsetEnabled}
                  onChange={(e) =>
                    onSetOffsetEnabled && onSetOffsetEnabled(e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Create multiple nodes with offset"
            />
          </div>

          {offsetEnabled && (
            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  Number of nodes (N):
                </label>
                <TextField
                  type="number"
                  inputProps={{ min: 1 }}
                  value={offsetCount}
                  onChange={(e) =>
                    onSetOffsetCount &&
                    onSetOffsetCount(
                      Math.max(1, parseInt(e.target.value || "1", 10))
                    )
                  }
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                {(["x", "y", "z"] as const).map((axis) => (
                  <div key={`off-${axis}`} style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontWeight: 500,
                      }}
                    >
                      {axis.toUpperCase()} offset:
                    </label>
                    <div style={{ position: "relative" }}>
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={offsetValues ? String(offsetValues[axis]) : "0"}
                        onChange={(e) =>
                          onSetOffsetValue &&
                          onSetOffsetValue(
                            axis,
                            parseFloat(e.target.value || "0")
                          )
                        }
                        InputProps={{
                          style: { paddingRight: "35px" },
                        }}
                      />
                      {/* Unit label for offset */}
                      <span
                        style={{
                          position: "absolute",
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "14px",
                          color: "#999",
                          pointerEvents: "none",
                        }}
                      >
                        {getLengthUnitLabel()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained">
          Create Node
        </Button>
      </DialogActions>
    </Dialog>
  );
};
